import { useEffect, useState } from 'react';
import { z } from 'zod';
import { RequestHook } from '../../components/availabilityWidget/api/bookingsApi';
import {
  PriceOffer,
  Prices,
  PricesAccommodationMap,
  GuestPriceInfo,
  FixedPrice,
  UsageBasedPrice,
  adviceValues,
} from './types';

const priceOffer = z.object({
  accommodation_id: z.number(),
  arrival: z.string(),
  checkin: z.string(),
  checkout: z.string(),
  departure: z.string(),
  price: z.number(),
  price_total: z.number(),
  suggestion_line: z.string(),
});

const usageBasedPrice = z.object({
  title: z.string(),
  price: z.number(),
  unit: z.string(),
  advice: z.nullable(z.enum(adviceValues)),
});

const fixedPrice = usageBasedPrice.omit({ advice: true }).extend({
  quantity: z.number(),
  total_price: z.number(),
});

const anyPrice = z.union([fixedPrice, usageBasedPrice]);

const travelBookingPriceResponse = z.object({
  security_deposit: z.number(),
  security_deposit_refund: z.nullable(z.enum(['instantly', 'one_week', 'two_weeks'])),
  security_deposit_payment_type: z.enum(['on_location', 'upfront']),
  price_rent: z.number(),
  total_price: z.number(),
  payable_casapilot: z.array(fixedPrice),
  payable_host: z.array(fixedPrice),
  optional: z.array(anyPrice),
  withheld_from_deposit: z.array(usageBasedPrice),
});

const priceOptionResponse = z.object({
  matching_suggestion: z.nullable(priceOffer),
  matching_price: z.nullable(travelBookingPriceResponse),
  alternative_suggestions: z.array(priceOffer),
});

const priceApiResponse = z.object({
  arrival: z.string(),
  duration_of_stay: z.nullable(z.number()),
  max_occupancy: z.number(),
  travel_suggestions: z.record(z.string(), priceOptionResponse),
});

const dateFormatOpts: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};
const dateTimeFormat = new Intl.DateTimeFormat('de', dateFormatOpts);

// legacy workaround support to handle the existing mechanism of the price actions
// should be removed when possible
export const arrivalUndefinedAsString = 'undefined';

function buildPricesSearchParams(
  accommodationIds: number[],
  useSuggestions: boolean,
  showFullPrice: boolean,
  arrival?: Date | string,
  durationOfStay?: number,
  occupancy?: number,
): URLSearchParams {
  const params = new URLSearchParams();
  accommodationIds.forEach((accId) => {
    params.append('acc_ids[]', String(accId));
  });
  if (arrival != null && arrival !== arrivalUndefinedAsString) {
    const arrivalValue = typeof arrival === 'string' ? arrival : dateTimeFormat.format(arrival);
    params.append('arrival', arrivalValue);
  }
  if (durationOfStay != null) {
    params.append('duration_of_stay', String(durationOfStay));
  }
  if (occupancy != null) {
    params.append('max_occupancy', String(occupancy));
  }
  if (useSuggestions) {
    params.append('use_suggestions', 'true');
  }
  if (showFullPrice) {
    params.append('show_full_price', 'true');
  }
  return params;
}

function parsePriceOffer(suggestion: z.infer<typeof priceOffer>): PriceOffer {
  return {
    accommodationID: suggestion.accommodation_id,
    arrival: suggestion.arrival,
    checkin: suggestion.checkin,
    checkout: suggestion.checkout,
    departure: suggestion.departure,
    price: suggestion.price,
    priceTotal: suggestion.price_total,
    suggestionLine: suggestion.suggestion_line,
  };
}

function parseFixedPrice(price: z.infer<typeof fixedPrice>): FixedPrice {
  return {
    title: price.title,
    price: price.price,
    unit: price.unit,
    quantity: price.quantity,
    totalPrice: price.total_price,
  };
}

function parseUsageBasedPrice(price: z.infer<typeof usageBasedPrice>): UsageBasedPrice {
  return {
    title: price.title,
    price: price.price,
    unit: price.unit,
    advice: price.advice,
  };
}

function parseAnyPrice(price: z.infer<typeof anyPrice>): FixedPrice | UsageBasedPrice {
  if ('quantity' in price) {
    return parseFixedPrice(price);
  }
  return parseUsageBasedPrice(price);
}

function parseTravelBookingPrice(
  price: z.infer<typeof travelBookingPriceResponse> | null,
): GuestPriceInfo | null {
  if (!price) {
    return null;
  }
  return {
    securityDeposit: price.security_deposit,
    securityDepositRefund: price.security_deposit_refund,
    securityDepositPaymentType: price.security_deposit_payment_type,
    priceRent: price.price_rent,
    totalPrice: price.total_price,
    payableCasapilot: price.payable_casapilot.map(parseFixedPrice),
    payableHost: price.payable_host.map(parseFixedPrice),
    optional: price.optional.map(parseAnyPrice),
    withheldFromDeposit: price.withheld_from_deposit.map(parseUsageBasedPrice),
  };
}

export async function fetchPrices(
  locale: string,
  accommodationIds: number[],
  useSuggestions: boolean,
  showFullPrice: boolean,
  arrival?: Date | string,
  durationOfStay?: number,
  occupancy?: number,
): Promise<PricesAccommodationMap> {
  const params = buildPricesSearchParams(
    accommodationIds,
    useSuggestions,
    showFullPrice,
    arrival,
    durationOfStay,
    occupancy,
  );
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };
  const path = `/api/internal/${encodeURIComponent(
    locale,
  )}/accommodations/price_suggestions?${params.toString()}`;

  const response = await fetch(path, init);
  if (!response.ok) {
    throw new Error(`Could not fetch prices: ${response.statusText}`);
  }

  const json = priceApiResponse.parse(await response.json());
  const prices: PricesAccommodationMap = {};
  accommodationIds.forEach((accId) => {
    const suggestion = json.travel_suggestions[accId.toString()];
    const price: Prices = {
      arrival: json.arrival,
      durationOfStay: json.duration_of_stay,
      maxOccupancy: json.max_occupancy,
      matchingSuggestion:
        suggestion.matching_suggestion && parsePriceOffer(suggestion.matching_suggestion),
      matchingPrice: parseTravelBookingPrice(suggestion.matching_price),
      alternativeSuggestions: suggestion.alternative_suggestions.map((sugg) =>
        parsePriceOffer(sugg),
      ),
      hasMatchingPrice: !!(suggestion?.matching_suggestion?.price != null),
    };
    prices[accId] = price;
  });

  return prices;
}

export async function fetchPricesForAccommodation(
  locale: string,
  accommodationId: number,
  showFullPrice: boolean,
  arrival?: Date,
  durationOfStay?: number,
  occupancy?: number,
): Promise<Prices> {
  const multiAccommodationResult = await fetchPrices(
    locale,
    [accommodationId],
    true,
    showFullPrice,
    arrival,
    durationOfStay,
    occupancy,
  );

  return multiAccommodationResult[accommodationId];
}

function buildArrivalKey(arrival?: Date | string | number): string | undefined {
  if (arrival == null) {
    return undefined;
  }
  if (arrival instanceof Date) {
    return dateTimeFormat.format(arrival);
  }
  if (typeof arrival === 'number') {
    return dateTimeFormat.format(new Date(arrival));
  }
  return undefined;
}

export function useFetchPrices(
  locale: string,
  accommodationId: number,
  showFullPrice: boolean,
  arrival?: Date,
  durationOfStay?: number,
  occupancy?: number,
): RequestHook<Prices> {
  const [data, setData] = useState<Prices | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);

  const arrivalKey = buildArrivalKey(arrival);

  useEffect(() => {
    if (data != null) {
      setData(undefined);
    }
    if (error != null) {
      setError(undefined);
    }
    fetchPricesForAccommodation(
      locale,
      accommodationId,
      showFullPrice,
      arrival,
      durationOfStay,
      occupancy,
    )
      .then((d) => setData(d))
      .catch((e) => setError(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, accommodationId, arrivalKey, durationOfStay, occupancy]);

  return { data, error };
}
